<template>
  <BaseDialog
    centered
    contentClass="seao-filters"
    ref="dialog"
    :show-actions="true"
    :persistent="!hasNoChanges"
    :on-close="closeModal"
    @closeModal="closeModal"
  >
    <template slot="title">{{ $t('dialog.filters.advanced.title') }} (SEAO)</template>
    <template slot="content">
      <div class="seao-filters__content">
        <div class="seao-filters__section">
          <div class="seao-filters__section--title">{{ $t('search-filters.zone.name') }}</div>
          <div class="seao-filters__choices">
            <FormCheckbox
              v-for="(region, index) in regions"
              :key="index"
              class="seao-filters__checkbox"
              :label="$t(`search-filters.seao-administrative-region.${region.name}`)"
              :value="region.value"
              @input="onRegionChange(region, index)"
              size="small"
            />
          </div>
        </div>
        <div class="seao-filters__section">
          <div class="seao-filters__section--title">{{ $t('search-filters.classifications.name') }}</div>
          <div class="seao-filters__classifications">
            <BaseMultiSelectSearch
              ref="classificationsMenu"
              :suggestions="displayedClassifications"
              :existing-selections="savedSeaoClassificationsTitles"
              :loading="classificationsLoading"
              :allow-add-custom="false"
              :empty-selections-label="$t('search-filters.classifications.none-selected')"
              :empty-suggestions-label="$t('search-filters.classifications.empty-result')"
              :description-label="$t('search-filters.classifications.description')"
              @updated-selected-names="onSeaoClassificationsChange"
              @on-search="onSeaoClassificationsSearch"
              @all-names-selected="onAllSeaoClassificationsSelected"
            />
          </div>
        </div>
      </div>
    </template>

    <template slot="actions">
      <div class="seao-filters__actions">
        <BaseButton @click.native="onClearAll" isTransparent isUnderline isSquare>
          {{ $t('dialog.filters.advanced.clear-all') }}
        </BaseButton>
        <BaseButton @click.native="applyChange" :disabled="hasNoChanges">{{
          $t('dialog.filters.advanced.apply-all')
        }}</BaseButton>
      </div>
    </template>
  </BaseDialog>
</template>

<script>
import BaseButton from '@/components/base/BaseButton';
import BaseDialog from '@/components/base/BaseDialog';
import { SEAO_ADMINISTRATIVE_REGIONS } from '@/models/search-filters';
import FormCheckbox from '@/components/form/FormCheckbox';

import { mapActions, mapGetters } from 'vuex';
import BaseMultiSelectSearch from '@/components/base/BaseMultiSelectSearch.vue';

export default {
  name: 'SEAOFiltersDialog',
  components: {
    BaseMultiSelectSearch,
    BaseButton,
    BaseDialog,
    FormCheckbox,
  },
  data() {
    return {
      isDirty: false,
      displayedClassifications: [],
      tempRegions: [],
      tempSearchFilters: {
        regions: [],
        classifications: [],
      },
    };
  },
  props: {
    explore: {
      type: Boolean,
      default: false,
    },
    searchFilters: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapGetters('keywords', ['keywordCount']),
    ...mapGetters('classifications', ['classifications']),
    ...mapGetters('classifications', { classificationsLoading: 'pending' }),
    hasNoChanges() {
      return !this.isDirty;
    },
    regions() {
      return Object.keys(SEAO_ADMINISTRATIVE_REGIONS).map((region) => ({
        name: region,
        value: this.searchFilters.administrativeRegions['seao_v2']
          ? this.searchFilters.administrativeRegions['seao_v2'].includes(region)
          : false,
      }));
    },
    seaoClassifications() {
      const seaoClassifications = this.classifications.find((classification) => classification.scheme === 'SEAO');
      return seaoClassifications ? seaoClassifications : null;
    },
    savedSeaoClassificationsTitles() {
      const classificationFilters = this.searchFilters.classifications || [];
      const seaoClassifications = classificationFilters.find((classification) => classification.scheme === 'SEAO');
      return seaoClassifications && this.seaoClassifications
        ? this.seaoClassifications.values
            .filter((saved) => seaoClassifications.codes.includes(saved.code))
            .map((saved) => this.formatClassification(saved))
        : [];
    },
  },
  methods: {
    ...mapActions('classifications', ['getClassifications']),
    applyChange() {
      this.$emit('set-search-filters', this.tempSearchFilters);
      this.closeModal();
    },
    onRegionChange(region, index) {
      this.isDirty = true;

      this.tempRegions[index].value = !this.tempRegions[index].value;

      if (this.tempRegions[index].value) {
        this.tempSearchFilters.regions.push(region.name);
      } else {
        const idx = this.tempSearchFilters.regions.indexOf(region.name);
        this.tempSearchFilters.regions.splice(idx, 1);
      }
    },
    onSeaoClassificationsChange(values) {
      this.isDirty = true;
      const codes = this.seaoClassifications
        ? this.seaoClassifications.values
            .filter((v) => values.includes(this.formatClassification(v)))
            .map((v) => v.code)
        : [];
      this.tempSearchFilters.classifications =
        codes.length > 0
          ? [
              {
                scheme: this.seaoClassifications.scheme,
                codes,
              },
            ]
          : [];
    },
    onSeaoClassificationsSearch(search) {
      this.isDirty = true;
      this.displayedClassifications = this.seaoClassifications
        ? this.seaoClassifications.values
            .filter((v) => this.formatClassification(v).toLowerCase().includes(search.toLowerCase()))
            .map((v) => this.formatClassification(v))
        : [];
    },
    onAllSeaoClassificationsSelected() {
      this.isDirty = true;
      this.tempSearchFilters.classifications = [];
    },
    closeModal() {
      this.isDirty = false;
      this.$emit('close-dialog');
    },
    restore() {
      let searchFilters = JSON.parse(JSON.stringify(this.searchFilters));
      this.tempRegions = this.regions;
      this.tempSearchFilters = {
        regions: searchFilters.administrativeRegions['seao_v2'] ? searchFilters.administrativeRegions['seao_v2'] : [],
        classifications: searchFilters.classifications,
      };
    },
    onClearAll() {
      this.isDirty = true;
      this.$refs.classificationsMenu.reset();
      this.tempRegions.forEach((region) => (region.value = false));
      this.tempSearchFilters = {
        regions: [],
        classifications: [],
      };
    },
    formatClassification(classification) {
      return `${classification.code} - ${classification.description}`;
    },
  },
  async created() {
    await this.getClassifications({ limit: -1, scheme: 'SEAO' });
    this.$nextTick(() => {
      this.$refs.dialog.isModalOpen = true;
      this.restore();
    });
  },
  watch: {
    seaoClassifications: {
      immediate: true,
      handler() {
        this.displayedClassifications = this.seaoClassifications
          ? this.seaoClassifications.values.map((c) => this.formatClassification(c))
          : [];
      },
    },
  },
};
</script>

<style lang="scss">
.base-dialog__seao-filters {
  width: 850px;
  max-height: 80vh !important;
  background: $color-white;
  position: relative;
  height: auto;

  .base-dialog__header {
    border-bottom: 1px solid $color-border;
  }

  .base-dialog__content {
    padding: 32px;
    font-size: 18px;
    background: $color-white !important;
  }

  .base-dialog__actions {
    padding: 32px;
    background: transparent;
    border-top: 1px solid $color-border;
  }

  @media screen and (max-width: 1080px) {
    width: 100%;
  }

  .__suggestion-container {
    overflow-y: visible;
  }
}

.seao-filters {
  &__section {
    margin-bottom: 32px;

    &--title {
      font-size: 20px;
      font-weight: bold;
    }

    &--subtitle {
      font-size: 18px;
      padding-top: 32px;
    }

    &--text {
      font-size: 16px;
      color: $color-neutral;
    }

    &--last {
      border-bottom: none;
    }
  }

  &__checkbox {
    width: 240px;
    padding: 16px 0;
  }

  &__actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__country {
    padding-top: 16px;
  }

  &__clear {
    text-decoration: underline;
    padding: 6px 8px;
    border-radius: $border-radius-root;
    cursor: pointer;
    transition: all 200ms ease;
    font-weight: bold;

    &:hover {
      background: $color-hover;
      transition: all 200ms ease;
    }
  }

  &__choices {
    padding: 16px 0;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;

    &.empty {
      font-size: 14px;
      color: $color-normal-pale;
      font-weight: normal;
      padding: 32px 0;
    }

    &--regions {
      padding: 0;
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
    }
  }

  &__classifications {
    padding: 16px 0;
  }
}
</style>
